import type { bakery, OrderCancelledCause, OrderSummaryState } from '@croissants/contracts';
import {
  type Locale,
  type MessageKey,
  type MessageParams,
  type ParameterizedKey,
  t,
} from '@croissants/i18n';
import type { JSX } from 'react';
import {
  PILL_FAMILIES,
  PILL_REASONS,
  PILL_WORD_KEYS,
  type PillFamily,
  type PillReason,
  type PillState,
} from './pill-list';
import { Badge, type BadgeProps, type BadgeTone, cx } from './primitives';

/*
 * The pill a state wears, the word and colour it reads, and the reason beside
 * it (#2004).
 *
 * The LIST itself - every family, its states and their colours, the keys
 * merged states share, the reasons each state can carry, and the two allow
 * lists - is `pill-list.ts`: pure data with type-only imports, so a plain
 * Node script reads the same list the screens draw (`check-counter-pills`
 * holds the Sunmi handheld to it, `build-pill-list` writes
 * docs/pill-list.html from it). This file is what a screen calls.
 */
export {
  PILL_FAMILIES,
  PILL_REASONS,
  PILL_WORD_KEYS,
  type PillFamily,
  type PillReason,
  type PillState,
} from './pill-list';

type WordKeys = typeof PILL_WORD_KEYS;

/** The catalogue key a state's word is read from. */
export type PillWordKey<F extends PillFamily, S extends PillState<F>> = F extends keyof WordKeys
  ? S extends keyof WordKeys[F]
    ? WordKeys[F][S]
    : `pill.${F}.${S}`
  : `pill.${F}.${S}`;

type ParamsOfKey<K> = K extends ParameterizedKey ? MessageParams<K> : never;

/**
 * The placeholders a state's word needs ("{count} unpriced", "{count}
 * left"), typed from the catalogue exactly as `t()` types them; `never` for a
 * word that needs none.
 */
export type PillParams<F extends PillFamily, S extends PillState<F>> = ParamsOfKey<
  PillWordKey<F, S>
>;

/** `pillWord`'s trailing argument: the placeholders, when the word has any, and nothing otherwise. */
export type PillParamsArg<F extends PillFamily, S extends PillState<F>> = [
  PillParams<F, S>,
] extends [never]
  ? []
  : [params: PillParams<F, S>];

/** `StatusPill`'s `params` prop: required when the word has placeholders, absent otherwise. */
export type PillParamsProp<F extends PillFamily, S extends PillState<F>> = [
  PillParams<F, S>,
] extends [never]
  ? { readonly params?: undefined }
  : { readonly params: PillParams<F, S> };

/** The catalogue key a reason's words are read from. */
export type PillReasonKey<F extends PillFamily, R extends string> = `pill.${F}.reason.${R}`;

/** The placeholders a reason's words need ("Closure through {date}"); `never` when none. */
export type PillReasonParams<F extends PillFamily, S extends PillState<F>> = ParamsOfKey<
  PillReasonKey<F, PillReason<F, S>>
>;

/**
 * `StatusPill`'s reason props. `reason` names one of the reasons the state
 * can carry (`PILL_REASONS`); `withReason` prints the state's reason when it
 * has exactly one, so a merged state brings its old word along without the
 * screen naming it. A reason with placeholders takes `reasonParams`.
 */
export type PillReasonProps<F extends PillFamily, S extends PillState<F>> = {
  readonly reason?: PillReason<F, S> | null;
  readonly withReason?: boolean;
} & ([PillReasonParams<F, S>] extends [never]
  ? { readonly reasonParams?: undefined }
  : { readonly reasonParams?: PillReasonParams<F, S> });

/** The catalogue key a state's word is read from: `PILL_WORD_KEYS`, else `pill.<family>.<state>`. */
export function pillWordKey<F extends PillFamily>(family: F, state: PillState<F>): MessageKey {
  const explicit = (PILL_WORD_KEYS as Partial<Record<string, Partial<Record<string, MessageKey>>>>)[
    family
  ]?.[state];
  // The key set is pinned by status-pill.test.tsx, in both locales.
  return explicit ?? (`pill.${family}.${state}` as MessageKey);
}

/** `t()` without its overloads: the key and its placeholders are typed by the callers' signatures. */
const translate = t as unknown as (
  locale: Locale,
  key: MessageKey,
  params?: Readonly<Record<string, string | number>>,
) => string;

/** The colour a state is drawn in: the list's, and nothing a screen chooses. */
export function pillTone<F extends PillFamily>(family: F, state: PillState<F>): BadgeTone {
  return (PILL_FAMILIES[family] as Record<string, BadgeTone>)[state] as BadgeTone;
}

/**
 * The word a state reads as, for the places that need it as text: a table's
 * sort and search, a CSV column, an accessible name. A word with placeholders
 * ("{count} left") takes them as a fourth argument, typed against the
 * catalogue as `t()` types them.
 */
export function pillWord<F extends PillFamily, S extends PillState<F>>(
  family: F,
  state: S,
  locale: Locale,
  ...params: PillParamsArg<F, S>
): string {
  return translate(
    locale,
    pillWordKey(family, state),
    params[0] as Readonly<Record<string, string | number>> | undefined,
  );
}

/** Every reason a state can carry, in the list's order. */
export function pillReasons<F extends PillFamily, S extends PillState<F>>(
  family: F,
  state: S,
): readonly PillReason<F, S>[] {
  const byState = (
    PILL_REASONS as Partial<Record<string, Partial<Record<string, readonly string[]>>>>
  )[family];
  return (byState?.[state] ?? []) as readonly PillReason<F, S>[];
}

/** The state's reason when it has exactly one - a merged state's old word - and null otherwise. */
export function soleReason<F extends PillFamily, S extends PillState<F>>(
  family: F,
  state: S,
): PillReason<F, S> | null {
  const reasons = pillReasons(family, state);
  return reasons.length === 1 ? (reasons[0] as PillReason<F, S>) : null;
}

/** The catalogue key a reason's words are read from. */
export function pillReasonKey(family: PillFamily, reason: string): MessageKey {
  return `pill.${family}.reason.${reason}` as MessageKey;
}

/**
 * A reason's words, as text: for a sort, a search, a title. `null` when the
 * state carries no reason (or several and none was named).
 */
export function pillReasonText<F extends PillFamily, S extends PillState<F>>(
  family: F,
  state: S,
  locale: Locale,
  reason?: PillReason<F, S> | null,
  params?: PillReasonParams<F, S>,
): string | null {
  const named = reason ?? soleReason(family, state);
  if (named === null) return null;
  return translate(
    locale,
    pillReasonKey(family, named),
    params as Readonly<Record<string, string | number>> | undefined,
  );
}

/** What an order's reason is read from: the cause it recorded, and a courier search still running. */
export interface OrderReasonFacts {
  /** Why it was canceled, as the order recorded it (`OrderLifecycleWire.cancelledCause`). */
  readonly cancelledCause?: OrderCancelledCause | null;
  /** When the running courier search gives up (`DashboardRow.courierSearchDeadline`), or null. */
  readonly courierSearchDeadline?: string | null;
}

/** `StatusPill`'s reason props for an order's summary state (`orderSummaryReasonOf`). */
export type OrderSummaryReasonProps<S extends OrderSummaryState = OrderSummaryState> = {
  readonly reason: PillReason<'orderSummary', S> | null;
} & ([PillReasonParams<'orderSummary', S>] extends [never]
  ? { readonly reasonParams?: undefined }
  : { readonly reasonParams?: PillReasonParams<'orderSummary', S> });

/*
 * Every cause the order machine can record has a reason beside Canceled: a
 * cause added to `OrderCancelledCause` without one is a typecheck failure here,
 * not a Canceled pill with nothing beside it.
 */
type CauseIsAReason =
  OrderCancelledCause extends PillReason<'orderSummary', 'CANCELLED'> ? true : never;
const EVERY_CAUSE_HAS_A_REASON: CauseIsAReason = true;
void EVERY_CAUSE_HAS_A_REASON;

/*
 * The Sunmi's line under its word is the wire's `BakeryOutcomeReason`, both
 * ways: a reason the platform sends without a line on the list, or a line the
 * platform can never send, is a typecheck failure here.
 */
type CounterLines =
  | PillReason<'counterOutcome', 'CANCELLED'>
  | PillReason<'counterOutcome', 'REFUSED'>;
type CounterLinesMatchTheWire = [bakery.BakeryOutcomeReason] extends [CounterLines]
  ? [CounterLines] extends [bakery.BakeryOutcomeReason]
    ? true
    : never
  : never;
const EVERY_COUNTER_REASON_HAS_A_LINE: CounterLinesMatchTheWire = true;
void EVERY_COUNTER_REASON_HAS_A_LINE;

/**
 * THE ONE PLACE a screen asks why an order reads as it does (#2004, approved
 * 2026-10-08, section 3): the reason beside its summary state, or null.
 *
 * Canceled carries the cause the order RECORDED, never one inferred here; a
 * refused card is "Card refused" whether the order was canceled for it or is
 * still open; and a Ready order whose courier came to nothing says until when
 * the platform keeps searching - the deadline the order record's banner
 * prints - or only "No courier yet" once nobody is searching (an operator
 * cancelled the job). `clock` formats that deadline in the reader's locale,
 * on the 24-hour clock every surface uses.
 */
export function orderSummaryReasonOf<S extends OrderSummaryState>(
  state: S,
  facts: OrderReasonFacts,
  clock: (instant: string) => string,
): OrderSummaryReasonProps<S> {
  return reasonOfState(state, facts, clock) as OrderSummaryReasonProps<S>;
}

function reasonOfState(
  state: OrderSummaryState,
  facts: OrderReasonFacts,
  clock: (instant: string) => string,
): OrderSummaryReasonProps {
  switch (state) {
    case 'CANCELLED':
      return { reason: facts.cancelledCause ?? null };
    case 'UNALLOCATED':
      return {
        reason:
          facts.cancelledCause === 'CASCADE_EXHAUSTED' ||
          facts.cancelledCause === 'NO_ELIGIBLE_HOUSE'
            ? facts.cancelledCause
            : null,
      };
    case 'PAYMENT_FAILED':
      return { reason: 'NEVER_PAID' };
    case 'COURIER_UNAVAILABLE': {
      const deadline = facts.courierSearchDeadline ?? null;
      return deadline === null
        ? { reason: 'NO_COURIER_YET' }
        : { reason: 'SEARCHING_UNTIL', reasonParams: { time: clock(deadline) } };
    }
    default:
      return { reason: null };
  }
}

/** An order's reason as text in a locale (`orderSummaryReasonOf`), or null when it has none. */
export function orderSummaryReasonText(
  state: OrderSummaryState,
  facts: OrderReasonFacts,
  locale: Locale,
  clock: (instant: string) => string,
): string | null {
  const { reason, reasonParams } = orderSummaryReasonOf(state, facts, clock);
  if (reason === null) return null;
  return translate(locale, pillReasonKey('orderSummary', reason), reasonParams);
}

/**
 * A filter's options for a family, one per WORD (#2004): the approved wording
 * reads several states as one word (Awaiting bakery is four order phases),
 * and a filter listing "Awaiting bakery" four times offers four choices that
 * look the same. Each option's value is the first state of its word;
 * `expand` turns the ticked options back into every state they stand for,
 * and `collapse` turns a set of states into the options to show ticked.
 */
export interface PillFilter<S extends string> {
  readonly options: readonly {
    readonly value: S;
    readonly label: string;
    readonly states: readonly S[];
  }[];
  /** The ticked options, as every state each one stands for. */
  readonly expand: (values: readonly string[]) => S[];
  /** A set of states, as the options that stand for them. */
  readonly collapse: (values: readonly string[]) => S[];
}

export function pillFilter<F extends PillFamily, S extends PillState<F>>(
  family: F,
  states: readonly S[],
  locale: Locale,
): PillFilter<S> {
  const byWord = new Map<string, S[]>();
  for (const state of states) {
    const word = translate(locale, pillWordKey(family, state));
    byWord.set(word, [...(byWord.get(word) ?? []), state]);
  }
  const options = [...byWord].map(([label, group]) => ({
    value: group[0] as S,
    label,
    states: group,
  }));
  const groupOf = new Map<string, readonly S[]>();
  for (const option of options)
    for (const state of option.states) groupOf.set(state, option.states);
  return {
    options,
    expand: (values) => [...new Set(values.flatMap((value) => [...(groupOf.get(value) ?? [])]))],
    collapse: (values) => [
      ...new Set(
        values.flatMap((value) => {
          const group = groupOf.get(value);
          return group === undefined ? [] : [group[0] as S];
        }),
      ),
    ],
  };
}

export type StatusPillProps<F extends PillFamily, S extends PillState<F> = PillState<F>> = Omit<
  BadgeProps,
  'tone' | 'children'
> & {
  readonly family: F;
  readonly state: S;
  readonly locale: Locale;
} & PillParamsProp<F, S> &
  PillReasonProps<F, S>;

/**
 * The pill a state wears, wherever it is shown - and, when asked, the reason
 * beside it as ordinary text (`cib-pill-reason`), never inside the pill.
 */
export function StatusPill<F extends PillFamily, S extends PillState<F>>(
  props: StatusPillProps<F, S>,
): JSX.Element {
  const { family, state, locale, params, reason, withReason, reasonParams, ...badge } =
    props as StatusPillProps<F, S> & {
      readonly params?: Readonly<Record<string, string | number>>;
      readonly reasonParams?: Readonly<Record<string, string | number>>;
    };
  const pill = (
    <Badge {...badge} tone={pillTone(family, state)}>
      {translate(locale, pillWordKey(family, state), params)}
    </Badge>
  );
  const named = reason ?? (withReason === true ? soleReason(family, state) : null);
  if (named === null || named === undefined) return pill;
  return (
    <span className="cib-pill-with-reason">
      {pill}
      <span className="cib-pill-reason">
        {translate(locale, pillReasonKey(family, named), reasonParams)}
      </span>
    </span>
  );
}

/*
 * ---------------------------------------------------------------------------
 * DATA PILLS: a value in a pill, never a word
 * ---------------------------------------------------------------------------
 */

/**
 * A pill whose content is a VALUE rather than a word (#2004): a CSV column's
 * own header, a star count, the instant a dead letter was replayed, the
 * rehearsal strip's clock offset and adapter. There is nothing to word - the
 * value is printed as it is, in no locale - so none of it belongs in the
 * catalogue or in `PILL_FAMILIES`. What still belongs to this file is the
 * COLOUR, so a screen names the KIND of value and this list colours it, the
 * same rule as a status pill. `chip` is the rehearsal strip's muted
 * monospace chip, which carries no colour at all.
 */
interface DataPillValues {
  /** A raw CSV column header a rejected import row names (Partner's import). */
  readonly csvColumn: string;
  /** A guest's star rating, 1 to 5 (Internal's Quality). */
  readonly starRating: number;
  /** When a dead-lettered event was replayed, formatted by the screen (Internal's Dead letter). */
  readonly replayedAt: string;
  /** The rehearsal strip's clock offset, adapter and next courier event. */
  readonly rehearsal: string;
}
export type DataPillKind = keyof DataPillValues;
export type DataPillValue<K extends DataPillKind> = DataPillValues[K];
/** A data pill's look: a badge colour, or the uncoloured rehearsal chip. */
export type DataPillTone = BadgeTone | 'chip';

export const DATA_PILLS: {
  readonly [K in DataPillKind]: DataPillTone | ((value: DataPillValues[K]) => BadgeTone);
} = {
  csvColumn: 'danger',
  /* Two stars or fewer is a complaint, three is lukewarm, four and five are fine. */
  starRating: (stars) => (stars <= 2 ? 'danger' : stars <= 3 ? 'warning' : 'success'),
  replayedAt: 'success',
  rehearsal: 'chip',
};

/** The look a data pill of this kind takes for this value. */
export function dataPillTone<K extends DataPillKind>(
  kind: K,
  value: DataPillValue<K>,
): DataPillTone {
  const rule = DATA_PILLS[kind] as DataPillTone | ((value: DataPillValue<K>) => BadgeTone);
  return typeof rule === 'function' ? rule(value) : rule;
}

export interface DataPillProps<K extends DataPillKind>
  extends Omit<BadgeProps, 'tone' | 'children'> {
  readonly kind: K;
  readonly value: DataPillValue<K>;
}

/** A value in a pill, coloured by its kind (`DATA_PILLS`), printed as it is. */
export function DataPill<K extends DataPillKind>({
  kind,
  value,
  className,
  dot,
  ...rest
}: DataPillProps<K>): JSX.Element {
  const tone = dataPillTone(kind, value);
  if (tone === 'chip') {
    return (
      <span {...rest} className={cx('cib-data-chip', className)}>
        {String(value)}
      </span>
    );
  }
  return (
    <Badge {...rest} {...(dot === undefined ? {} : { dot })} className={className} tone={tone}>
      {String(value)}
    </Badge>
  );
}
